import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useHelpContent } from '../hooks/useHelpContent';
import { renderMarkdown } from './markdown';

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-out help drawer for the public portal (mirrors the admin HelpPanel; no lucide dep). ≤ 400px wide, full width on mobile (BR-WEB-007).
 * Portalled to document.body so it is always viewport-anchored: an ancestor with a transform, filter, will-change or
 * layout/paint containment becomes the containing block for position:fixed descendants, and the panel would then anchor
 * to the top of the page and open off-screen for a scrolled visitor. e2e/home.spec.ts opens it after scrolling down.
 */
export function HelpPanel({ open, onClose }: HelpPanelProps) {
  const { pathname } = useLocation();
  const { title, content, loading, error } = useHelpContent(pathname);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return createPortal(
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-panel-title"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[400px] bg-white shadow-lg border-l border-gray-200 flex flex-col transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 id="help-panel-title" className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-xl leading-none text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            aria-label="Close help"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <HelpSkeleton />}
          {error && !loading && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && content && <article className="prose-help">{renderMarkdown(content)}</article>}
          {!loading && !error && !content && <p className="text-sm text-gray-500">No help content for this page yet.</p>}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 text-xs text-gray-500">
          Still have questions? Send us your enquiry and a local rep will get back to you within 2 business days.
        </footer>
      </aside>
    </>,
    document.body,
  );
}

function HelpSkeleton() {
  return (
    <div className="animate-pulse space-y-3" aria-hidden="true">
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-5/6" />
      <div className="h-3 bg-gray-200 rounded w-4/6" />
    </div>
  );
}
